/**
 * Pure utility functions extracted from ContentManagementProject.
 * No React, no state, no side effects.
 */

// ─── CSV ────────────────────────────────────────────────────────────────

export function stripOuterQuotes(value: string): string {
  let out = value.trim();
  while (out.length >= 2 && out.startsWith('"') && out.endsWith('"')) {
    out = out.slice(1, -1).trim();
  }
  return out;
}

export function parseCsvLine(line: string, delimiter: string): string[] {
  const values: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      const next = line[i + 1];
      if (inQuotes && next === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (ch === delimiter && !inQuotes) {
      values.push(current);
      current = '';
      continue;
    }
    current += ch;
  }
  values.push(current);
  return values;
}

export function parseCsv(raw: string): { columns: string[]; rows: Record<string, string>[] } {
  const text = raw.trim();
  if (!text) return { columns: [], rows: [] };
  const lines = text.split(/\r?\n/).filter(Boolean);
  if (lines.length === 0) return { columns: [], rows: [] };
  const delimiter = lines[0].includes(';') && !lines[0].includes(',') ? ';' : ',';
  const rawColumns = parseCsvLine(lines[0], delimiter).map(h => h.trim());
  const columns = rawColumns.filter(col => col.toLowerCase() !== 'trend');
  const rows = lines.slice(1).map(line => {
    const values = parseCsvLine(line, delimiter);
    const row: Record<string, string> = {};
    rawColumns.forEach((col, idx) => {
      if (col.toLowerCase() === 'trend') return;
      row[col] = stripOuterQuotes((values[idx] || '').trim());
    });
    return row;
  });
  return { columns, rows };
}

export function toCsvValue(value: string): string {
  if (value == null) return '';
  const needsQuotes = /[",\n\r]/.test(value);
  const escaped = value.replace(/"/g, '""');
  return needsQuotes ? `"${escaped}"` : escaped;
}

export function buildCsvString(columns: string[], rows: Record<string, string>[]): string {
  if (!columns.length) return '';
  const header = columns.join(',');
  const body = rows.map(row => columns.map(col => toCsvValue(row[col] || '')).join(',')).join('\n');
  return [header, body].filter(Boolean).join('\n');
}

